import React from "react";
import { Box, Button, Paper, Typography } from "@mui/material";

/* Catches a render/lifecycle crash anywhere below it and shows a recoverable
 * screen instead of a blank white page. Without this, one thrown error in any
 * of the module screens takes the whole app down with no explanation and no way
 * back except a manual browser reload.
 *
 * Deliberately NOT a data-loss warning: an error here happens while drawing the
 * page, after any save has already gone to the server, so nothing typed is lost
 * by reloading. The copy says that plainly, because the first thing anyone
 * wonders when a screen breaks mid-invoice is whether their work survived.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Left in the browser console so it can be read off a user's screen during
    // a support call. Wire this to a real error tracker when one is in place.
    console.error("Screen crashed:", error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          bgcolor: "background.default",
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 520, width: "100%" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            This screen stopped working
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            Anything you already saved is safe on the server — this went wrong
            while drawing the page, not while saving. Reloading will bring you
            back to a working screen.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 3,
              fontFamily: "monospace",
              color: "text.disabled",
              wordBreak: "break-word",
            }}
          >
            {String(this.state.error?.message || this.state.error)}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Reload the page
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                window.location.href = "/main";
              }}
            >
              Back to the dashboard
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }
}
